// CardMetricTailwind.jsx - version améliorée avec icône interne et édition
import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { Search, Settings } from 'lucide-react';

function formatDateFr(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (isNaN(d)) return dateString;
  return d.toLocaleDateString('fr-FR');
}

export default function CardMetric({
  icon,
  title,
  value,
  valueColor = 'text-primary',
  caption,
  format = true,
  detailDialog,
  editable = false,
  storageKey = '', // clé unique pour le localStorage si editable
  onValueChange,
}) {
  // État interne pour les cards sans gestion externe
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = detailDialog?.isOpen !== undefined ? detailDialog.isOpen : internalOpen;
  const setIsOpen = detailDialog?.setIsOpen || setInternalOpen;

  // Edition custom
  const [editOpen, setEditOpen] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [customValue, setCustomValue] = useState('');

  // Charger depuis le localStorage au montage
  useEffect(() => {
    if (editable && storageKey) {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setCustomTitle(parsed.title || '');
          setCustomValue(parsed.value || '');
        } catch {}
      }
    }
  }, [editable, storageKey]);

  // Sauvegarder dans le localStorage
  const saveCustom = () => {
    if (editable && storageKey) {
      localStorage.setItem(storageKey, JSON.stringify({ title: customTitle, value: customValue }));
      if (onValueChange) {
        onValueChange(Number(customValue));
      }
    }
    setEditOpen(false);
  };

  const displayValue = (typeof value === 'number' && format)
    ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(value)
    : value;

  const handleOpenDetail = async () => {
    if (detailDialog?.onOpen) {
      await detailDialog.onOpen();
    } else {
      setIsOpen(true);
    }
  };

  // Si c'est le tableau Qonto, on reformate la date dans le contenu
  let detailContent = detailDialog?.content;
  if (detailDialog?.title?.toLowerCase().includes('qonto') && Array.isArray(detailDialog?.content?.props?.children)) {
    const table = detailDialog.content.props.children.find(
      el => el && el.type === 'table'
    );
    if (table) {
      const newTable = {
        ...table,
        props: {
          ...table.props,
          children: [
            table.props.children[0],
            Array.isArray(table.props.children[1])
              ? table.props.children[1].map(row => {
                  if (!row || !row.props || !Array.isArray(row.props.children)) return row;
                  const newCells = row.props.children.map((cell, idx) =>
                    idx === 0 && cell && typeof cell.props?.children === 'string'
                      ? { ...cell, props: { ...cell.props, children: formatDateFr(cell.props.children) } }
                      : cell
                  );
                  return { ...row, props: { ...row.props, children: newCells } };
                })
              : table.props.children[1]
          ]
        }
      };
      detailContent = {
        ...detailDialog.content,
        props: {
          ...detailDialog.content.props,
          children: detailDialog.content.props.children.map(child =>
            child && child.type === 'table' ? newTable : child
          )
        }
      };
    }
  }

  // Affichage du titre et du montant custom si editable
  const finalTitle = editable && customTitle ? customTitle : title;
  const finalValue = editable && customValue !== '' ? Number(customValue) : value;

  return (
    <div className="relative bg-white rounded-md shadow-sm w-[200px] px-3 py-2 flex flex-col justify-start gap-2">
      {/* Engrenage en haut à droite si editable */}
      {editable && (
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
          onClick={() => setEditOpen(true)}
          title="Éditer la carte"
        >
          <Settings className="w-4 h-4" />
        </button>
      )}
      {/* Icon + Title inline */}
      <div className="flex items-center gap-2 text-sm text-gray-500 font-medium truncate">
        {icon && (
          <div className="bg-gray-100 text-gray-600 rounded-full w-6 h-6 flex items-center justify-center shadow-sm">
            {icon}
          </div>
        )}
        <span className="truncate">{finalTitle}</span>
      </div>

      {/* Value + Action */}
      <div className="flex items-center justify-between">
        <div className={`text-xl font-bold ${valueColor}`}>{(typeof finalValue === 'number' && format)
          ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(finalValue)
          : finalValue}</div>
        {detailDialog && (
          <button onClick={handleOpenDetail} className="text-secondary hover:text-secondary-dark">
            {detailDialog.actionIcon || <Search className="w-4 h-4" />}
          </button>
        )}
      </div>

      {/* Dotted separator */}
      <div className="border-t border-dotted border-gray-300 w-full" />

      {/* Caption */}
      {caption && (
        <div className="text-xs text-gray-500 truncate">
          {caption}
        </div>
      )}

      {/* Modal de détail */}
      {detailDialog && (
        <Dialog open={isOpen} onClose={() => setIsOpen(false)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="bg-white rounded shadow-lg max-w-4xl w-full max-h-[80vh] p-4 flex flex-col" style={{ minHeight: 200 }}>
              <Dialog.Title className="text-lg font-semibold mb-2">{detailDialog.title}</Dialog.Title>
              <div className="flex-1 min-h-0 overflow-y-auto">{detailContent}</div>
              <div className="text-right mt-4 sticky bottom-0 bg-white pt-2">
                <button onClick={() => setIsOpen(false)} className="px-4 py-2 bg-secondary text-white rounded hover:bg-secondary-dark">
                  Fermer
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      )}

      {/* Modal d'édition */}
      {editable && (
        <Dialog open={editOpen} onClose={() => setEditOpen(false)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="bg-white rounded shadow-lg max-w-xs w-full p-4 flex flex-col gap-3">
              <Dialog.Title className="text-lg font-semibold mb-2">Éditer la carte</Dialog.Title>
              <label className="text-xs text-gray-600">Titre</label>
              <input
                className="border rounded px-2 py-1 text-sm"
                value={customTitle}
                onChange={e => setCustomTitle(e.target.value)}
                placeholder="Titre de la carte"
              />
              <label className="text-xs text-gray-600 mt-2">Montant (€)</label>
              <input
                className="border rounded px-2 py-1 text-sm"
                type="number"
                step="any"
                value={customValue}
                onChange={e => setCustomValue(e.target.value)}
                placeholder="Montant (ex: 123.45 ou -50)"
              />
              <div className="text-right mt-4">
                <button onClick={() => setEditOpen(false)} className="px-3 py-1 mr-2 text-gray-600 hover:text-gray-900">Annuler</button>
                <button onClick={saveCustom} className="px-4 py-1 bg-secondary text-white rounded hover:bg-secondary-dark">Enregistrer</button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      )}
    </div>
  );
}
